import DOMPurify from 'dompurify';

/**
 * 入力された値がundefinedであることをチェックします。
 *
 * @param val 検査値
 * @returns {boolean} valがundefinedの場合true
 */
export function isUndefined(val) {
  return val === undefined;
}

/**
 * 入力された値がundefinedでないことをチェックします。
 *
 * @param val 検査値
 * @returns {boolean} valがundefinedでないの場合true
 */
export function isNotUndefined(val) {
  return !isUndefined(val);
}

/**
 * 入力された値がnullであることをチェックします。
 * isUndefinedの検査も行います。
 *
 * @param val 検査値
 * @returns {boolean} valがnullの場合true
 */
export function isNull(val) {
  return isUndefined(val) || val === null;
}

/**
 * 入力された値がnullでないことをチェックします。
 * isNotUndefinedの検査も行います。
 *
 * @param val 検査値
 * @returns {boolean} valがnullでない場合true
 */
export function isNotNull(val) {
  return !isNull(val);
}

/**
 * 入力された値が空であることをチェックします。
 * isNullの検査も行います。
 *
 * @param val 検査値
 * @returns {boolean} valが空の場合true
 */
export function isEmpty(val) {
  if (isNull(val)) return true;
  if (val instanceof Array) {
    return val.length === 0;
  } else if (val instanceof Object) {
    return Object.keys(val).length === 0;
  } else {
    return val === '';
  }
}

/**
 * 入力された値が空でないことをチェックします。
 * isNotNullの検査も行います。
 *
 * @param val 検査値
 * @returns {boolean} valが空でない場合true
 */
export function isNotEmpty(val) {
  return !isEmpty(val);
}

/**
 * 入力された値がブランクであることをチェックします。
 * isEmptyの検査も行います。
 *
 * @param val 検査値
 * @returns {boolean} valがブランクの場合true
 */
export function isBlank(val) {
  if (isNull(val)) return true;
  if (typeof (val) === 'string') {
    return isEmpty(val.trim());
  } else {
    return isEmpty(val);
  }
}

/**
 * 入力された値がブランクでないことをチェックします。
 * isNotEmptyの検査も行います。
 *
 * @param val 検査値
 * @returns {boolean} valがブランクでない場合true
 */
export function isNotBlank(val) {
  return !isBlank(val);
}

/**
 * 頭文字を大文字に変換します。
 *
 * @param val
 * @returns {string}
 */
export function toUpperCaseFirstChar(val) {
  return val[0].toUpperCase() + val.slice(1);
}

/**
 * 頭文字を小文字に変換します。
 *
 * @param val
 * @returns {string}
 */
export function toLowerCaseFirstChar(val) {
  return val[0].toLowerCase() + val.slice(1);
}

/**
 * オブジェクトから指定されたプロパティに該当する値を取得します。
 *
 * @param obj オブジェクト
 * @param properties プロパティ
 * @returns {*|undefined}
 */
export function getNestedVal(obj, ...properties) {
  if (isNull(obj)) return undefined;
  properties = properties.flat();
  let current = obj;
  for (const property of properties) {
    const val = current[property];
    if (isUndefined(val)) {
      return undefined;
    } else {
      current = val;
    }
  }
  return current;
}

/**
 * オブジェクトに指定されたプロパティが存在する場合、trueを返却します。
 * 指定されたプロパティに該当する値がundefinedの場合もfalseが返却されます。
 *
 * @param obj
 * @param properties
 * @returns {true | false}
 */
export function existsProperty(obj, ...properties) {
  return isNotUndefined(getNestedVal(obj, properties));
}

/**
 * 空文字列をハイフンに変換します。
 *
 * @param val
 * @returns {'-'|*} 空文字列の場合'-'、それ以外の場合はval
 */
export function blankToHyphen(val) {
  return isBlank(val) ? '-' : val;
}

/**
 * nullの場合ハイフンに変換します。
 *
 * @param val
 * @returns {'-'|*} nullの場合'-'、それ以外の場合はval
 */
export function nullToHyphen(val) {
  return isNull(val) ? '-' : val;
}

/**
 * objがArrayまたはObjectならDeepCopyを行います
 * objがArray,Object以外の場合、objをそのまま返却します
 *
 * @param {*} obj
 */
export function copy(obj) {
  if (isNull(obj)) return obj;
  if (obj instanceof Array || obj instanceof Object) {
    const jsonStr = JSON.stringify(obj);
    if (isNull(jsonStr)) return obj;
    return JSON.parse(jsonStr);
  } else {
    return obj;
  }
}

/**
 * blank値をnullに変換します。
 *
 * @param obj
 * @param trimming trueの場合、値をtrimする。
 * @returns {(*|{}|String|obj)[]|String|obj|*|{}}
 */
export function blankToNull(obj, trimming = false) {
  if (obj instanceof Array) {
    return obj.map(elem => blankToNull(elem, trimming))
      .filter(elem => (elem instanceof Array || elem instanceof Object) ? true : isNotBlank(elem));
  } else if (obj instanceof Object) {
    const temp = {};
    for (const key in obj) {
      const val = blankToNull(obj[key], trimming);
      temp[key] = !(val instanceof Array) && isEmpty(val) ? null : val;
    }
    return temp;
  } else {
    return isEmpty(obj) ? null : trimming ? trim(obj) : obj;
  }
}

/**
 * ブラウザの言語設定を取得します
 *
 * @return {'ja' | 'en'} ブラウザの言語設定
 */
export function getLanguage() {
  const judge = ((window.navigator.languages && window.navigator.languages[0])
    || window.navigator.language
    || window.navigator.userLanguage
    || window.navigator.browserLanguage);

  return (judge === 'ja-JP' || judge === 'ja') ? 'ja' : 'en';
}

/**
 * objがStringの場合に両端の空白を除去します
 * objがString以外の場合、objをそのまま返却します
 *
 * @param {*} obj
 *
 * @return { String | obj}
 */
export function trim(obj) {
  if (typeof (obj) === 'string') return obj.trim();
  else return obj;
}

/**
 * 指定された時間を待つPromiseを返却します。
 *
 * @param ms 待ち時間(ms)
 * @returns {Promise<unknown>}
 */
export function sleep(ms) {
  return new Promise((resolve) => setTimeout(() => resolve(), ms));
}

/**
 * バイト数に応じてB,kB,MB,GB,TB,PB表示に変換したファイルサイズを返却します。
 * 有効数字3桁に変換し、少数部の末尾の0は除外します。
 *
 * @param size ファイルサイズ
 * @returns {String}
 */
export function toFixedFileSize(size) {
  let unit = '';
  let convertedSize = '';

  // 1000を冪として単位を切り替えて、有効数字3桁に変換する
  if (size < 1000) {
    convertedSize = size.toPrecision(3);
    unit = ' B';
  } else if (size < 1000 ** 2) {
    convertedSize = (size / (1000)).toPrecision(3);
    unit = ' kB';
  } else if (size < 1000 ** 3) {
    convertedSize = (size / (1000 ** 2)).toPrecision(3);
    unit = ' MB';
  } else if (size < 1000 ** 4) {
    convertedSize = (size / (1000 ** 3)).toPrecision(3);
    unit = ' GB';
  } else if (size < 1000 ** 5) {
    convertedSize = (size / (1000 ** 4)).toPrecision(3);
    unit = ' TB';
  } else {
    convertedSize = (size / (1000 ** 5)).toPrecision(3);
    unit = ' PB';
  }
  // 少数部の末尾に0および小数点が含まれている場合に除外する
  if (convertedSize.includes('.')) convertedSize = convertedSize.replace(/[0]+$/, '').replace(/[.]$/, '');
  return convertedSize + unit;
}

const ALLOWED_TAGS = ['b', 'li', 'ol', 'ul', 'p', 'strong', 'em', 'del', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'pre', 'code', 'blockquote', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr', 'a', 'img'];

/**
 * HTMLのサニタイズを行います。
 *
 * @param html
 * @param allowedTags 許可するタグの配列。defaultでは{@link ALLOWED_TAGS} に定義されたタグのみ有効化されます。
 * @returns {*|String|string}
 */
export function sanitizeHtml(html, allowedTags = ALLOWED_TAGS) {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: allowedTags });
}

/**
 * キャメルケースをケバブケースに変換します。
 *
 * @param camelStr
 * @returns {string}
 */
export function camelToKebab(camelStr) {
  return toLowerCaseFirstChar(camelStr).replace(/([A-Z])/g, str => '-' + str.charAt(0).toLowerCase());
}

/**
 * キャメルケースをスネークケースに変換します。
 * 
 * @param camelStr
 * @returns {string}
 */
export function camelToSnake(camelStr) {
  return toLowerCaseFirstChar(camelStr).replace(/([A-Z])/g, str => '_' + str.charAt(0).toLowerCase());
}

/**
 * 使用しているブラウザがIEかチェックします。
 * @returns {boolean} 使用しているブラウザがIEの場合true
 */
export function isIE() {
  const ua = window.navigator.userAgent;
  const msie = ua.indexOf('MSIE ');
  const msie11 = ua.indexOf('Trident/');
  return msie > 0 || msie11 > 0;
}